import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, FileSpreadsheet, Boxes, ClipboardCheck, Loader2, Filter } from "lucide-react";

const OPTIONS = [
  { k: "catalog", icon: FileSpreadsheet, title: "Tout le catalogue (Excel)", desc: "Tous les articles avec prix et stock par entrepôt, à ouvrir dans un tableur ou à transférer.", url: "/inventory-export", ok: "Catalogue exporté" },
  { k: "complete", icon: Boxes, title: "Inventaire complet (PDF)", desc: "Tout le matériel à imprimer : nom, catégorie et quantités par entrepôt.", url: "/inventory-print/complete", ok: "Inventaire complet téléchargé" },
  { k: "selection", icon: Filter, title: "Sélection filtrée (PDF)", desc: "", url: "/inventory-print/selection", ok: "Sélection exportée" },
  { k: "pending", icon: ClipboardCheck, title: "Inventaire à faire (PDF)", desc: "La fiche de comptage des inventaires en attente, avec quantité attendue et cases à remplir.", url: "/inventory-print/pending", ok: "Fiche de comptage téléchargée" },
];

async function blobError(e) {
  try { return apiError(JSON.parse(await e.response.data.text()).detail); } catch { return ""; }
}

export function ExportDialog({ pendingCount, selection }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const run = async (o) => {
    setBusy(o.k);
    try {
      const { data, headers } = o.k === "selection"
        ? await api.post(o.url, { ids: selection.ids, filters: selection.label }, { responseType: "blob" })
        : await api.get(o.url, { responseType: "blob" });
      const name = (headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "export";
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success(o.ok);
      setOpen(false);
    } catch (e) { toast.error((await blobError(e)) || "Export impossible"); }
    finally { setBusy(""); }
  };
  return (
    <>
      <Button data-testid="stock-export-btn" variant="outline" onClick={() => setOpen(true)} className="rounded-full border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-800">
        <Download className="mr-1.5 h-4 w-4" />Exporter
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="stock-export-dialog" className="border-zinc-800 bg-zinc-950 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Exporter</DialogTitle>
            <DialogDescription className="text-zinc-500">Choisissez ce que vous voulez télécharger.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {OPTIONS.map((o) => {
              const off = (o.k === "pending" && !pendingCount) || (o.k === "selection" && !selection?.ids.length);
              const desc = o.k === "selection" ? `${selection?.ids.length || 0} article(s) · ${selection?.label || "Aucun filtre"}` : o.desc;
              return (
                <button key={o.k} data-testid={`export-option-${o.k}`} disabled={!!busy || off} onClick={() => run(o)}
                  className="flex w-full items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900 p-4 text-left transition-colors hover:border-[#FF6A00]/60 disabled:cursor-not-allowed disabled:opacity-50">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FF6A00]/10 text-[#FF6A00]">
                    {busy === o.k ? <Loader2 className="h-4 w-4 animate-spin" /> : <o.icon className="h-4 w-4" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-white">{o.title}</span>
                    <span className="mt-0.5 block text-xs text-zinc-500">{off ? (o.k === "selection" ? "Aucun article ne correspond aux filtres." : "Aucun inventaire en attente pour le moment.") : desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
